import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { useParams } from "react-router-dom";
import {
  LayersControl,
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
  Legend,
  ReferenceDot,
} from "recharts";

import { aprsLogbookTrackById } from "../common/ApiUtils";
import { logBookDayToDdMmYyyy } from "../common/TimeUtils";
import {
  decimateTrack,
  findNearestIndexByTMs,
  gapBridgePositions,
  parseIgc,
  splitTrackByGap,
  trackEdgeSubsegments,
  toEpochMs,
} from "./aprsTrackUtils";

const API_TRACK_MAX_POINTS = 12000;
const MAP_RENDER_MAX_POINTS = 1500;
const CHART_RENDER_MAX_POINTS = 1350;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const MAP_BASE_LAYERS = [
  {
    id: "osm",
    name: "Standardní mapa",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  },
  {
    id: "topo",
    name: "Topografická (reliéf, výšky)",
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>, ' +
      '<a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA), SRTM',
    maxZoom: 17,
  },
  {
    id: "esri-topo",
    name: "Terénní (vrstevnice)",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri, Garmin, NOAA, USGS",
    maxZoom: 18,
  },
  {
    id: "satellite",
    name: "Letecká / satelit",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri, Maxar, Earthstar Geographics",
    maxZoom: 19,
  },
];

const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length) {
      map.fitBounds(bounds, { padding: [24, 24] });
    }
  }, [map, bounds]);
  return null;
};

const LOGBOOK_META_EMPTY = {
  identifier: null,
  key: null,
  day: null,
  takeOff: null,
  landing: null,
  duration: null,
  takeoffPlace: null,
  landingPlace: null,
  hasIgcFile: false,
  ognDescriptor: null,
};

function normalizeOgnDescriptor(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    regNumber: raw.regNumber ?? null,
    cn: raw.cn ?? null,
    owner: raw.owner ?? null,
    homeBase: raw.homeBase ?? null,
    model: raw.model ?? null,
    freq: raw.freq ?? null,
    known: !!raw.known,
    tracked: !!raw.tracked,
    identified: !!raw.identified,
  };
}

function normalizeTrackResponse(raw) {
  return {
    ...LOGBOOK_META_EMPTY,
    identifier: raw?.identifier ?? null,
    key: raw?.key ?? null,
    day: raw?.day ?? null,
    takeOff: raw?.takeOff ?? null,
    landing: raw?.landing ?? null,
    duration: raw?.duration ?? null,
    takeoffPlace: raw?.takeoffPlace ?? null,
    landingPlace: raw?.landingPlace ?? null,
    hasIgcFile: !!raw?.hasIgcFile,
    ognDescriptor: normalizeOgnDescriptor(raw?.ognDescriptor),
    points: normalizeTrackPoints(raw),
  };
}
function normalizeTrackPoints(raw) {
  let points = Array.isArray(raw) ? raw : raw?.points ?? raw?.track ?? raw?.data ?? raw ?? [];
  if (points && !Array.isArray(points) && typeof points === "object") {
    points = Object.values(points);
  }
  if (!Array.isArray(points)) return [];

  return points
    .map((p) => {
      const lat = Number(p?.lat ?? p?.latitude);
      const lon = Number(p?.lon ?? p?.lng ?? p?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

      const tMs = toEpochMs(
        p?.tMs ?? p?.tms ?? p?.t ?? p?.time ?? p?.ts ?? p?.timestamp ?? p?.dateTime
      );
      const msl = Number(
        p?.msl ??
          p?.alt ??
          p?.altitude ??
          p?.altMsl ??
          p?.heightMsl ??
          p?.altitudeMsl ??
          p?.altitudeMsl?.parsedValue ??
          p?.altitudeMsl?.value
      );
      const agl = Number(p?.agl ?? p?.heightAgl ?? p?.altAgl ?? p?.agl?.parsedValue ?? p?.agl?.value);
      const terrain = Number(
        p?.terrain ??
          p?.terrainMsl ??
          p?.groundMsl ??
          p?.groundElevation ??
          p?.terrainMsl?.parsedValue ??
          p?.terrainMsl?.value
      );
      const speed = Number(
        p?.speed ?? p?.spd ?? p?.groundSpeed ?? p?.speed?.parsedValue ?? p?.speed?.value
      );
      return {
        lat,
        lon,
        tMs,
        msl: Number.isFinite(msl) ? msl : null,
        terrain: Number.isFinite(terrain) ? terrain : null,
        agl: Number.isFinite(agl) ? agl : null,
        speed: Number.isFinite(speed) ? speed : null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.tMs ?? 0) - (b.tMs ?? 0));
}

function toLatLng(p) {
  return [p.lat, p.lon];
}

function formatUtcTime(tMs) {
  if (!Number.isFinite(tMs)) return "—";
  const d = new Date(tMs);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}Z`;
}

function formatNum(v, digits = 6) {
  return Number.isFinite(v) ? Number(v).toFixed(digits) : "—";
}

function formatMeters(v) {
  return Number.isFinite(v) ? `${Math.round(v)} m` : "—";
}

function formatSpeed(v) {
  return Number.isFinite(v) ? `${Math.round(v)} km/h` : "—";
}

function formatFlightTime(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "string") return v;
  const ms = toEpochMs(v);
  return ms != null ? formatUtcTime(ms) : String(v);
}

function TrackTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload;
  return (
    <div style={{ background: "white", border: "1px solid #ddd", padding: 8, fontSize: 12 }}>
      <div>
        <strong>{formatUtcTime(p?.tMs)}</strong>
      </div>
      <div>lat: {formatNum(p?.lat)}</div>
      <div>lon: {formatNum(p?.lon)}</div>
      <div>MSL: {formatMeters(p?.msl)}</div>
      <div>Terén: {formatMeters(p?.terrain)}</div>
      <div>AGL: {formatMeters(p?.agl)}</div>
      <div>speed: {formatSpeed(p?.speed)}</div>
    </div>
  );
}

function bearingDeg(a, b) {
  if (!a || !b) return 0;
  const toRad = (x) => (x * Math.PI) / 180;
  const toDeg = (x) => (x * 180) / Math.PI;
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLon = toRad(b.lon - a.lon);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  const brng = toDeg(Math.atan2(y, x));
  return (brng + 360) % 360;
}

/** Map pointer X position on chart to UTC ms. */
function tMsFromChartPointer(state, chartData) {
  if (!chartData?.length) return null;
  const label = state?.activeLabel;
  if (label != null && Number.isFinite(Number(label))) {
    return Number(label);
  }
  const chartX = state?.chartX;
  const chartWidth = state?.chartWidth;
  if (!Number.isFinite(chartX) || !Number.isFinite(chartWidth) || chartWidth <= 0) {
    return null;
  }
  const marginLeft = 56;
  const marginRight = 48;
  const plotW = chartWidth - marginLeft - marginRight;
  const x = chartX - marginLeft;
  if (plotW <= 0 || x < 0 || x > plotW) return null;
  const tMin = chartData[0].tMs;
  const tMax = chartData[chartData.length - 1].tMs;
  if (!Number.isFinite(tMin) || !Number.isFinite(tMax) || tMax <= tMin) return tMin;
  return tMin + (x / plotW) * (tMax - tMin);
}

const GLIDER_SVG_PATH =
  "M91 142.31c-6.43-.65-8-1.28-8-3.25 0-1.95.78-2.24 9.9-3.62l4.4-.67-.69-21.09c-.39-11.61-.85-21.26-1.04-21.45-.19-.18-15.36-1.22-33.71-2.3-18.35-1.07-39.66-2.51-47.36-3.19C.56 85.51.5 85.49.5 83v-2.5l17.5-.63c9.62-.34 30.83-.68 47.11-.75L94.73 79l.48-9.25c.32-6.08 1.16-10.79 2.45-13.75l1.97-4.5 1.79 2.74c1.21 1.85 2.16 6.35 2.89 13.75L105.39 79l29.56.12c16.25.07 37.43.41 47.05.75l17.5.63V83c0 2.48-.11 2.51-13 3.66-7.15.64-25.82 1.88-41.5 2.76s-31.27 1.85-34.66 2.16l-6.16.56-.56 10.18c-.3 5.6-.78 15.21-1.06 21.36l-.51 11.18 5.73.7c6.57.79 9.22 1.92 9.22 3.91 0 .77-.79 1.63-1.75 1.92-2.34.7-19.84 1.37-24.25.92m10.79-72.73c.44-11.75-3.06-13.67-4.38-2.4-.84 7.12.05 11.07 2.38 10.63 1.41-.27 1.76-1.71 2-8.23";

/** Top-down glider (nose up in SVG); map bearing rotates the whole icon. */
function gliderSvgHtml({ size = 46, color = "#111827", rotationDeg = 0, opacity = 0.95 }) {
  return `
    <div style="
      width:${size}px;
      height:${size}px;
      transform: rotate(${rotationDeg}deg);
      transform-origin: 50% 50%;
      opacity:${opacity};
    ">
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 200 200">
        <path d="${GLIDER_SVG_PATH}" fill="${color}"/>
      </svg>
    </div>
  `;
}

function gliderDivIcon({ rotationDeg = 0, color = "#111827", size = 46 }) {
  return L.divIcon({
    className: "glider-svg-marker",
    html: gliderSvgHtml({ rotationDeg, color, size }),
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export default function AprsLogbookMapPage() {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [track, setTrack] = useState([]);
  const [logbookMeta, setLogbookMeta] = useState(LOGBOOK_META_EMPTY);
  const [idx, setIdx] = useState(0);
  const [gapThresholdSec, setGapThresholdSec] = useState(120);

  const [overlayName, setOverlayName] = useState(null);
  const [overlayPoints, setOverlayPoints] = useState([]);
  const [overlayOffsetSec, setOverlayOffsetSec] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await aprsLogbookTrackById(id, API_TRACK_MAX_POINTS);
        const normalized = normalizeTrackResponse(data);
        if (!mounted) return;
        const { points: pts, ...meta } = normalized;
        setLogbookMeta(meta);
        setTrack(pts);
        setIdx(0);
      } catch (e) {
        if (!mounted) return;
        setError(e?.statusText || "Nepodařilo se načíst trasu.");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  const minMax = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const p of track) {
      for (const v of [p.msl, p.terrain]) {
        if (Number.isFinite(v)) {
          min = Math.min(min, v);
          max = Math.max(max, v);
        }
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return { minMsl: 0, maxMsl: 1 };
    if (min === max) return { minMsl: min - 1, maxMsl: max + 1 };
    return { minMsl: min, maxMsl: max };
  }, [track]);

  const speedMinMax = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const p of track) {
      if (Number.isFinite(p.speed)) {
        min = Math.min(min, p.speed);
        max = Math.max(max, p.speed);
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return { minSpeed: 0, maxSpeed: 120 };
    if (min === max) return { minSpeed: Math.max(0, min - 5), maxSpeed: max + 5 };
    return { minSpeed: Math.max(0, min), maxSpeed: max };
  }, [track]);

  const bounds = useMemo(() => {
    const pts = track.length ? track : overlayPoints;
    if (!pts.length) return null;
    return pts.map(toLatLng);
  }, [track, overlayPoints]);

  const mapTrack = useMemo(
    () => decimateTrack(track, MAP_RENDER_MAX_POINTS),
    [track]
  );
  const chartTrack = useMemo(
    () => decimateTrack(track, CHART_RENDER_MAX_POINTS),
    [track]
  );

  const gapThresholdMs = (Number(gapThresholdSec) || 120) * 1000;
  const segments = useMemo(
    () => splitTrackByGap(mapTrack, gapThresholdMs),
    [mapTrack, gapThresholdMs]
  );

  const chartData = useMemo(
    () =>
      chartTrack.map((p) => ({
        tMs: p.tMs,
        msl: p.msl,
        terrain: p.terrain,
        agl: p.agl,
        lat: p.lat,
        lon: p.lon,
        speed: p.speed,
      })),
    [chartTrack]
  );

  const mapPolylines = useMemo(() => {
    const lines = [];
    const gapDash = { color: "#64748b", weight: 3, dashArray: "10 8", opacity: 0.85 };
    segments.forEach((seg, sIdx) => {
      const pts = seg.points;
      if (!pts?.length) return;
      if (pts.length >= 2) {
        for (let i = 1; i < pts.length; i++) {
          const a = pts[i - 1];
          const b = pts[i];
          trackEdgeSubsegments(a, b, minMax.minMsl, minMax.maxMsl).forEach((sub, subIdx) => {
            lines.push(
              <Polyline
                key={`${sIdx}-${i}-${subIdx}`}
                positions={sub.positions}
                pathOptions={{ color: sub.color, weight: 4 }}
              />
            );
          });
        }
      }
      if (seg.hasGapAfter) {
        const bridge = gapBridgePositions(pts, segments[sIdx + 1]?.points);
        if (bridge) {
          lines.push(
            <Polyline key={`${sIdx}-gap-bridge`} positions={bridge} pathOptions={gapDash} />
          );
        }
      }
    });
    return lines;
  }, [segments, minMax, gapThresholdSec]);

  const current = track[idx] ?? null;
  const currentHeading = useMemo(() => {
    if (!track.length || idx <= 0) return 0;
    const prev = track[idx - 1];
    const cur = track[idx];
    return bearingDeg(prev, cur);
  }, [track, idx]);

  const overlayWithOffset = useMemo(() => {
    if (!overlayPoints.length) return [];
    const baseT = overlayPoints[0]?.tMs ?? 0;
    const offsetMs = (Number(overlayOffsetSec) || 0) * 1000;
    return overlayPoints.map((p) => ({ ...p, tMs: (p.tMs ?? baseT) + offsetMs }));
  }, [overlayPoints, overlayOffsetSec]);

  const overlayCurrent = useMemo(() => {
    if (!overlayWithOffset.length || !current?.tMs) return null;
    let best = 0;
    let bestDt = Infinity;
    for (let i = 0; i < overlayWithOffset.length; i++) {
      const dt = Math.abs((overlayWithOffset[i].tMs ?? 0) - current.tMs);
      if (dt < bestDt) {
        bestDt = dt;
        best = i;
      }
    }
    return overlayWithOffset[best] ?? null;
  }, [overlayWithOffset, current]);

  const overlayHeading = useMemo(() => {
    if (!overlayWithOffset.length || !overlayCurrent) return 0;
    const i = overlayWithOffset.indexOf(overlayCurrent);
    if (i <= 0) return 0;
    return bearingDeg(overlayWithOffset[i - 1], overlayWithOffset[i]);
  }, [overlayWithOffset, overlayCurrent]);

  const onChartMouseMove = useCallback(
    (state) => {
      if (!track.length || !state) return;
      const tMs = tMsFromChartPointer(state, chartData);
      if (tMs != null) {
        const next = findNearestIndexByTMs(track, tMs);
        setIdx((prev) => (prev === next ? prev : next));
      }
    },
    [track, chartData]
  );

  const onOverlayFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const parsed = parseIgc(text);
    const baseDateMs = track[0]?.tMs ?? Date.now();
    const dayStart = new Date(baseDateMs);
    dayStart.setHours(0, 0, 0, 0);
    const dayStartMs = dayStart.getTime();

    const pts = parsed.points
      .map((p) => ({
        lat: p.lat,
        lon: p.lon,
        tMs: dayStartMs + p.timeSec * 1000,
        msl: Number.isFinite(p.msl) ? p.msl : null,
        agl: null,
      }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon) && Number.isFinite(p.tMs));

    setOverlayName(parsed.gliderId || file.name);
    setOverlayPoints(pts);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/aprslogbook" />
          </IonButtons>
          <IonTitle>
            Mapa letu
            {logbookMeta.day ? ` · ${logBookDayToDdMmYyyy(logbookMeta.day) ?? "—"}` : ""}
            {logbookMeta.identifier ? ` · ${logbookMeta.identifier}` : ""}
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {loading && (
          <div style={{ padding: 16 }}>
            <IonSpinner /> Načítám…
          </div>
        )}
        {!loading && error && <div style={{ padding: 16, color: "red" }}>{String(error)}</div>}

        {!loading && !error && (
          <>
            <div style={{ height: "52vh", width: "100%" }}>
              <MapContainer center={[49.6, 15.0]} zoom={10} style={{ height: "100%", width: "100%" }}>
                <LayersControl position="topright">
                  {MAP_BASE_LAYERS.map((layer, i) => (
                    <LayersControl.BaseLayer key={layer.id} name={layer.name} checked={i === 0}>
                      <TileLayer
                        url={layer.url}
                        attribution={layer.attribution}
                        maxZoom={layer.maxZoom}
                      />
                    </LayersControl.BaseLayer>
                  ))}
                </LayersControl>
                {bounds && <FitBounds bounds={bounds} />}

                {mapPolylines}

                {current && (
                  <Marker
                    position={toLatLng(current)}
                    icon={gliderDivIcon({ rotationDeg: currentHeading, color: "#111827", size: 48 })}
                  >
                    <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                      {formatUtcTime(current.tMs)} | MSL {formatMeters(current.msl)}
                      {current.agl != null ? ` | AGL ${formatMeters(current.agl)}` : ""}
                      {current.speed != null ? ` | ${formatSpeed(current.speed)}` : ""}
                    </Tooltip>
                  </Marker>
                )}

                {overlayWithOffset.length >= 2 && (
                  <>
                    <Polyline
                      positions={overlayWithOffset.map(toLatLng)}
                      pathOptions={{ color: "#8b5cf6", weight: 3, dashArray: "6 6" }}
                    />
                    {overlayCurrent && (
                      <Marker
                        position={toLatLng(overlayCurrent)}
                        icon={gliderDivIcon({ rotationDeg: overlayHeading, color: "#7c3aed", size: 44 })}
                      >
                        <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                          {overlayName || "overlay"}
                        </Tooltip>
                      </Marker>
                    )}
                  </>
                )}
              </MapContainer>
            </div>

            <div style={{ padding: "8px 12px 0", fontSize: 13 }}>
              <div>
                <strong>Datum</strong>: {logBookDayToDdMmYyyy(logbookMeta.day) ?? "—"}
                {" | "}
                <strong>Vzlet</strong>: {formatFlightTime(logbookMeta.takeOff)}
                {" → "}
                <strong>Přistání</strong>: {formatFlightTime(logbookMeta.landing)}
                {logbookMeta.duration ? (
                  <>
                    {" "}
                    | <strong>Délka</strong>: {logbookMeta.duration}
                  </>
                ) : null}
              </div>
              <div>
                {logbookMeta.takeoffPlace ? (
                  <>
                    <strong>Odkud</strong>: {logbookMeta.takeoffPlace}
                    {" "}
                  </>
                ) : null}
                {logbookMeta.landingPlace ? (
                  <>
                    <strong>Kam</strong>: {logbookMeta.landingPlace}
                  </>
                ) : null}
              </div>
              <div>
                {logbookMeta.identifier ? (
                  <>
                    <strong>Imatrikulace</strong>: {logbookMeta.identifier}
                    {" "}
                  </>
                ) : null}
                {logbookMeta.key ? (
                  <>
                    | <strong>OGN</strong>: {logbookMeta.key}
                  </>
                ) : null}
              </div>
              {logbookMeta.ognDescriptor && (
                <div style={{ color: "#475569", marginTop: 4 }}>
                  {logbookMeta.ognDescriptor.regNumber ? (
                    <>
                      OGN reg.: {logbookMeta.ognDescriptor.regNumber}
                      {" "}
                    </>
                  ) : null}
                  {logbookMeta.ognDescriptor.cn ? (
                    <>
                      | CN: {logbookMeta.ognDescriptor.cn}
                      {" "}
                    </>
                  ) : null}
                  {logbookMeta.ognDescriptor.model ? (
                    <>
                      | typ: {logbookMeta.ognDescriptor.model}
                      {" "}
                    </>
                  ) : null}
                  {logbookMeta.ognDescriptor.owner ? (
                    <>
                      | vlastník: {logbookMeta.ognDescriptor.owner}
                      {" "}
                    </>
                  ) : null}
                  {logbookMeta.ognDescriptor.homeBase ? (
                    <>| základna: {logbookMeta.ognDescriptor.homeBase}</>
                  ) : null}
                </div>
              )}
            </div>

            {current && (
              <div style={{ padding: "4px 12px 0", fontSize: 13 }}>
                <div>
                  <strong>UTC</strong>: {formatUtcTime(current.tMs)} | lat {formatNum(current.lat)} | lon {formatNum(current.lon)} | MSL {formatMeters(current.msl)} |
                  terén {formatMeters(current.terrain)} | AGL {formatMeters(current.agl)} | speed{" "}
                  {formatSpeed(current.speed)}
                </div>
              </div>
            )}

            <div style={{ padding: "8px 12px 12px" }}>
              {chartData.length > 0 ? (
                <div style={{ width: "100%", height: 240, cursor: "crosshair" }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <ComposedChart
                      data={chartData}
                      onMouseMove={onChartMouseMove}
                      throttleDelay={50}
                      isAnimationActive={false}
                    >
                      <XAxis
                        dataKey="tMs"
                        type="number"
                        domain={["dataMin", "dataMax"]}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(v) => formatUtcTime(v)}
                        minTickGap={28}
                      />
                      <YAxis
                        yAxisId="altitude"
                        tick={{ fontSize: 12 }}
                        domain={[minMax.minMsl, minMax.maxMsl]}
                        label={{ value: "m MSL", angle: -90, position: "insideLeft", offset: 10, fontSize: 11 }}
                      />
                      <YAxis
                        yAxisId="speed"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        domain={[speedMinMax.minSpeed, speedMinMax.maxSpeed]}
                        label={{ value: "km/h", angle: 90, position: "insideRight", offset: 10, fontSize: 11 }}
                      />
                      <ReTooltip content={<TrackTooltip />} />
                      <Legend />
                      <Area
                        yAxisId="altitude"
                        type="monotone"
                        dataKey="terrain"
                        name="Terén (m)"
                        stroke="#57534e"
                        fill="#a8a29e"
                        fillOpacity={1}
                        baseValue={minMax.minMsl}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls
                      />
                      <Line
                        yAxisId="altitude"
                        type="monotone"
                        dataKey="msl"
                        stroke="#2563eb"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 6 }}
                        name="MSL (m)"
                      />
                      <Line
                        yAxisId="speed"
                        type="monotone"
                        dataKey="speed"
                        stroke="#dc2626"
                        dot={false}
                        activeDot={{ r: 5 }}
                        name="Rychlost (km/h)"
                        connectNulls
                      />
                      {current && (
                        <>
                          <ReferenceDot
                            yAxisId="altitude"
                            x={current.tMs}
                            y={current.msl ?? minMax.minMsl}
                            r={5}
                            fill="#ef4444"
                            stroke="none"
                          />
                          {Number.isFinite(current.speed) && (
                            <ReferenceDot
                              yAxisId="speed"
                              x={current.tMs}
                              y={current.speed}
                              r={4}
                              fill="#dc2626"
                              stroke="none"
                            />
                          )}
                        </>
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ padding: 16, color: "#64748b" }}>Žádná data pro graf.</div>
              )}
            </div>

            <div style={{ padding: 12 }}>
              <IonItem>
                <IonLabel position="stacked">Gap threshold (s)</IonLabel>
                <IonInput
                  value={gapThresholdSec}
                  inputmode="numeric"
                  onIonInput={(e) => setGapThresholdSec(e.detail.value ?? 120)}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Overlay IGC</IonLabel>
                <input
                  type="file"
                  accept=".igc,text/plain"
                  onChange={(e) => onOverlayFile(e.target.files?.[0] ?? null)}
                />
              </IonItem>

              {overlayPoints.length > 0 && (
                <>
                  <IonItem>
                    <IonLabel position="stacked">Overlay offset (s)</IonLabel>
                    <IonInput
                      value={overlayOffsetSec}
                      inputmode="numeric"
                      onIonInput={(e) => setOverlayOffsetSec(e.detail.value ?? 0)}
                    />
                  </IonItem>
                  <IonButton
                    expand="block"
                    color="medium"
                    onClick={() => {
                      setOverlayName(null);
                      setOverlayPoints([]);
                      setOverlayOffsetSec(0);
                    }}
                  >
                    Odebrat overlay
                  </IonButton>
                </>
              )}
            </div>
          </>
        )}
      </IonContent>
    </IonPage>
  );
}

